// The form builder.
//
// Parity pass 2 batch 4; reshaped by the 2026-09-29 forms restyle. Sections
// hold questions; questions have a type, a label, and whatever settings that
// type needs.
//
// TWO THINGS THIS PAGE MUST GET RIGHT, and both are about keys:
//
//   * A KEY IS NEVER REGENERATED. It is the join between a question and every
//     answer already stored against it, so editing a label leaves the key
//     alone and deleting a question is a real, considered loss rather than an
//     accident. The server checks a key's shape and never invents one.
//     (Duplicating a question mints a FRESH key for the copy and touches
//     nothing on the source — `lib/formBuilder.ts`.)
//   * EDITING NEVER TOUCHES WHAT IS LIVE. Saving writes the draft; the frozen
//     published version keeps serving every fill until someone presses
//     Publish. The banner says so, because "I changed it and nothing happened"
//     is otherwise the obvious reading.
//
// THE FORM READS AS A LIST (the restyle): each question is a collapsed row —
// label, type, Required — expanded to edit. Everything typed lives in the
// controlled `content` document, so collapsing a row loses nothing. Reorder
// is drag (dnd-kit, handles only) AND buttons; both call the same pure moves
// in `lib/formBuilder.ts`, so the gesture and the menu cannot disagree, and
// jsdom pins the logic without simulating a drag.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  forms,
  newKey,
  publishProblemsFrom,
  ENTITY_KINDS,
  FIELD_TYPES,
  FIELD_TYPE_HINT,
  FIELD_TYPE_LABEL,
  type EntityKind,
  type FieldType,
  type FormContent,
  type FormField,
  type FormTemplate,
  type FormVersion,
  type FormVersionSummary,
} from '../formsApi';
import { ApiError } from '../api';
import { FormSections } from '../components/forms/FormSections';
import {
  duplicateField,
  moveFieldToSection,
  reorderFields,
  reorderSections,
  swapFields,
  swapSections,
} from '../lib/formBuilder';
import { machineTypes as machineTypesApi, type MachineType } from '../fleetApi';
import { ActionMenu, Modal, PageHeader, StatusBadge, useConfirm } from '../components/ui';
import type { AssetFilter } from '../formsApi';

/** The Yes / No / N/A preset — what almost every prestart question wants. */
const YES_NO_NA = ['Yes', 'No', 'N/A'];

type BuilderTab = 'build' | 'preview' | 'versions';

export function TemplateBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const confirm = useConfirm();
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [content, setContent] = useState<FormContent | null>(null);
  const [versions, setVersions] = useState<FormVersionSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<BuilderTab>('build');
  // Which question rows are expanded. Any number may be open at once, and
  // collapsing loses nothing: every keystroke lives in `content`.
  const [openKeys, setOpenKeys] = useState<ReadonlySet<string>>(new Set());
  // The section whose Add-question type palette is open.
  const [addingIn, setAddingIn] = useState<string | null>(null);
  // The question the Move-to-section dialog is about.
  const [moveTarget, setMoveTarget] = useState<{ si: number; fi: number } | null>(null);
  // The machine-types list behind the "Offered machines" grid. Null while
  // loading or unavailable — the grid hides, and an existing filter is left
  // untouched (the whole document round-trips, so hiding cannot strip it).
  const [typeOptions, setTypeOptions] = useState<MachineType[] | null>(null);
  const problemsRef = useRef<HTMLDivElement | null>(null);
  // A freshly added or duplicated question gets focus in its label input on
  // the render after it exists.
  const focusKey = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const result = await forms.getTemplate(id);
      setTemplate(result.template);
      setContent(result.template.content);
      setVersions(result.versions);
      setDirty(false);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that form.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // Archived included: a filter already naming an archived type shows it
    // ticked and labelled, rather than silently un-showing a live setting.
    void machineTypesApi
      .list({ includeArchived: true })
      .then((r) => setTypeOptions(r.machineTypes), () => setTypeOptions(null));
  }, []);

  // Unsaved edits guard the tab the way any editor does. The in-app links are
  // not guarded — round-tripping every click through a dialog is how people
  // learn to click through dialogs — but closing the tab loses real work.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // The publish problems render at the top of the page while Publish lives in
  // the header — from the bottom of a long form, a refused publish looked
  // like NOTHING HAPPENING, which is worse than an error. `behavior: 'auto'`,
  // not 'smooth': ZT measured smooth as a silent no-op in a scroll container,
  // and it is a no-op under reduced-motion — which would reintroduce the same
  // bug for exactly some people.
  useEffect(() => {
    if (problems.length > 0) {
      problemsRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [problems]);

  useEffect(() => {
    if (!focusKey.current) return;
    const el = document.getElementById(`label-${focusKey.current}`);
    focusKey.current = null;
    el?.focus();
  });

  const fieldCount = useMemo(
    () => (content?.sections ?? []).reduce((n, s) => n + s.fields.length, 0),
    [content]
  );

  function edit(next: FormContent) {
    setContent(next);
    setDirty(true);
    setProblems([]);
  }

  async function save() {
    if (!id || !content) return;
    setBusy(true);
    setError(null);
    try {
      const result = await forms.updateTemplate(id, { content });
      setTemplate(result.template);
      // Take the server's normalized copy back, not the local one: it is what
      // was actually stored, and anything the normalizer dropped should
      // disappear from the screen now rather than on the next reload.
      setContent(result.template.content);
      setDirty(false);
      setNotice('Saved as a draft.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  // Ctrl+S / Cmd+S saves the draft: an editor's muscle memory, honoured
  // rather than fought. Read through refs so the listener mounts once.
  const saveRef = useRef<() => void>(() => undefined);
  saveRef.current = () => {
    if (dirty && !busy) void save();
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function publish() {
    if (!id) return;
    const nextVersion = (template?.currentVersion?.version ?? 0) + 1;
    const sure = await confirm({
      title: 'Publish this form?',
      message: `Version ${nextVersion} will be saved and become the version new forms are filled against. Forms already filled in keep the version they were filled against.`,
      confirmLabel: 'Publish',
      tone: 'primary',
    });
    if (!sure) return;
    setBusy(true);
    setError(null);
    setProblems([]);
    try {
      if (dirty) await forms.updateTemplate(id, { content });
      const result = await forms.publishTemplate(id);
      setTemplate(result.template);
      setContent(result.template.content);
      setDirty(false);
      setNotice(`Published version ${result.version.version}. Your crew can fill it in now.`);
      // The history moved; the Versions tab should say so without a reload.
      void forms.getTemplate(id).then((r) => setVersions(r.versions), () => undefined);
    } catch (err) {
      const found = publishProblemsFrom(err);
      if (found.length > 0) {
        // Every problem at once. A manager fixing a form one error at a time
        // is a manager who stops fixing it.
        setProblems(found);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not publish that.');
      }
    } finally {
      setBusy(false);
    }
  }

  // Drag starts on the handle only, and only after a little movement, so a
  // tap on the handle is not a drag and a scroll on a phone is not a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!template || !content) {
    return error ? (
      <div className="banner-error" role="alert">
        {error}
      </div>
    ) : (
      <p className="muted">Loading…</p>
    );
  }

  const sections = content.sections;

  const setSections = (next: typeof sections) => edit({ ...content, sections: next });

  const updateField = (si: number, fi: number, patch: Partial<FormField>) =>
    setSections(
      sections.map((s, i) =>
        i !== si
          ? s
          : { ...s, fields: s.fields.map((f, j) => (j === fi ? { ...f, ...patch } : f)) }
      )
    );

  const toggleOpen = (key: string) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  function addField(si: number, type: FieldType) {
    // A brand new key, generated once and never again.
    const key = newKey('f');
    const base: FormField = { key, type, label: '', required: false };
    // A fresh choice question starts as Yes / No / N/A — what almost every
    // prestart question actually wants (round 28).
    if (type === 'choice') base.options = [...YES_NO_NA];
    // A default rather than a blank: the normalizer DROPS an entity question
    // with no entity, so a blank here would make it quietly vanish on save.
    if (type === 'entity_ref') base.entity = 'machine';
    setSections(sections.map((s, i) => (i === si ? { ...s, fields: [...s.fields, base] } : s)));
    setOpenKeys((prev) => new Set(prev).add(key));
    focusKey.current = key;
    setAddingIn(null);
  }

  function duplicate(si: number, fi: number) {
    const key = newKey('f');
    setSections(duplicateField(sections, si, fi, key));
    setOpenKeys((prev) => new Set(prev).add(key));
    focusKey.current = key;
  }

  async function removeSection(si: number) {
    const section = sections[si];
    if (!section) return;
    if (section.fields.length > 0) {
      const n = section.fields.length;
      const sure = await confirm({
        title: 'Remove this section?',
        message: `Its ${n} question${n === 1 ? '' : 's'} go with it. Forms already submitted keep the version they were filled against.`,
        confirmLabel: 'Remove section',
        tone: 'danger',
      });
      if (!sure) return;
    }
    setSections(sections.filter((_, i) => i !== si));
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const a = String(active.id);
    const o = String(over.id);
    if (a.startsWith('s:') && o.startsWith('s:')) {
      const from = sections.findIndex((s) => `s:${s.key}` === a);
      const to = sections.findIndex((s) => `s:${s.key}` === o);
      if (from >= 0 && to >= 0) setSections(reorderSections(sections, from, to));
      return;
    }
    if (a.startsWith('f:')) {
      // Within one section; moving BETWEEN sections is the menu's job.
      for (let si = 0; si < sections.length; si++) {
        const fields = sections[si]!.fields;
        const from = fields.findIndex((f) => `f:${f.key}` === a);
        if (from < 0) continue;
        const to = fields.findIndex((f) => `f:${f.key}` === o);
        if (to >= 0) setSections(reorderFields(sections, si, from, to));
        return;
      }
    }
  }

  return (
    <>
      <PageHeader
        title={template.name}
        subtitle={template.description ?? undefined}
        actions={
          <>
            {/* The pill says what the buttons' disabled states only imply. */}
            {dirty && (
              <span className="self-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                Unsaved changes
              </span>
            )}
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy || !dirty}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : dirty ? 'Save draft' : 'Saved'}
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy}
              onClick={() => void publish()}
            >
              Publish
            </button>
          </>
        }
      />

      <p className="mb-4 text-sm">
        <Link to="/app/forms/templates" className="table-link">
          &larr; All forms
        </Link>
      </p>

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="banner-ok mb-4" role="status">
          {notice}
        </div>
      )}

      {problems.length > 0 && (
        <div className="banner-error mb-4" role="alert" ref={problemsRef}>
          <strong>This form is not ready to publish</strong>
          <ul className="mt-1 list-disc pl-5">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {/* The state that is easy to misread as "my edit did nothing". */}
      {template.status === 'published' && (dirty || template.hasUnpublishedChanges) && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Your crew is still filling in{' '}
          <strong>version {template.currentVersion?.version}</strong>. These changes go live when
          you publish, and past submissions keep the questions they were answered against.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-600">
        <StatusBadge
          kind="expiry"
          status={template.status === 'published' ? 'ok' : 'soon'}
          label={template.status === 'published' ? 'Published' : 'Draft'}
        />
        <span>
          {fieldCount} question{fieldCount === 1 ? '' : 's'}
        </span>
      </div>

      {/* Build / Preview / Versions. The BUILD panel hides with `hidden`
          rather than unmounting, so half-typed builder state survives a look
          at the preview (round 28, ZT's measured fix); the preview itself is
          throwaway by design and can mount fresh. */}
      <div className="mb-4 flex flex-wrap items-center gap-2" role="tablist" aria-label="Builder">
        {(['build', 'preview', 'versions'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={tab === key ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
            onClick={() => setTab(key)}
          >
            {key === 'build' ? 'Build' : key === 'preview' ? 'Preview' : 'Versions'}
          </button>
        ))}
      </div>

      {tab === 'preview' && <TemplatePreview content={content} dirty={dirty} />}
      {tab === 'versions' && (
        <VersionsTab templateId={template.id} versions={versions} />
      )}

      <div hidden={tab !== 'build'} className="max-w-3xl">
      {/* Which machines this form is offered against. Asset forms only —
          retyping the form drops the filter with the meter question's
          legality, and the grid goes with it. */}
      {content.link_kind === 'asset' && typeOptions !== null && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-1 text-base font-semibold text-gray-900">Offered machines</h2>
          <p className="mb-3 text-sm text-gray-600">
            Leave everything unticked to offer every machine. Tick a kind, a machine type, or
            both; with both, a machine has to match each.
          </p>
          <AssetFilterGrid
            filter={content.asset_filter ?? null}
            typeOptions={typeOptions}
            onChange={(next) =>
              edit({
                ...content,
                ...(next ? { asset_filter: next } : {}),
                ...(next ? {} : { asset_filter: undefined }),
              })
            }
          />
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={sections.map((s) => `s:${s.key}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {sections.map((section, si) => (
              <SortableItem
                key={section.key}
                id={`s:${section.key}`}
                handleLabel="Drag to reorder this section"
              >
                {(handle) => (
                  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-end gap-2">
                      <div className="pb-0.5">{handle}</div>
                      <div className="min-w-0 flex-1">
                        <label className="form-label" htmlFor={`section-${section.key}`}>
                          Section
                        </label>
                        <input
                          id={`section-${section.key}`}
                          className="form-input"
                          value={section.title}
                          placeholder="Untitled (just runs the questions together)"
                          onChange={(e) =>
                            setSections(
                              sections.map((s, i) =>
                                i === si ? { ...s, title: e.target.value } : s
                              )
                            )
                          }
                        />
                      </div>
                      <ActionMenu
                        label="Section actions"
                        items={[
                          {
                            label: 'Move up',
                            disabled: si === 0,
                            onClick: () => setSections(swapSections(sections, si, -1)),
                          },
                          {
                            label: 'Move down',
                            disabled: si === sections.length - 1,
                            onClick: () => setSections(swapSections(sections, si, 1)),
                          },
                          {
                            label: 'Remove section',
                            tone: 'danger',
                            onClick: () => void removeSection(si),
                          },
                        ]}
                      />
                    </div>

                    <SortableContext
                      items={section.fields.map((f) => `f:${f.key}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {section.fields.map((field, fi) => (
                          <SortableItem
                            key={field.key}
                            id={`f:${field.key}`}
                            handleLabel="Drag to reorder this question"
                          >
                            {(fieldHandle) => (
                              <div className="rounded-md border border-gray-200 bg-white">
                                <div className="flex items-center gap-1 py-1 pl-1 pr-2 sm:gap-2">
                                  {fieldHandle}
                                  <button
                                    type="button"
                                    className="flex min-h-11 min-w-0 flex-1 flex-wrap items-center gap-x-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                                    aria-expanded={openKeys.has(field.key)}
                                    onClick={() => toggleOpen(field.key)}
                                  >
                                    <span
                                      className={
                                        field.label
                                          ? 'truncate text-sm font-medium text-gray-900'
                                          : 'text-sm font-medium text-amber-800'
                                      }
                                    >
                                      {field.label || 'Untitled question'}
                                    </span>
                                    <span className="flex-none text-xs text-gray-600">
                                      {fieldChip(field)}
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary btn-sm"
                                    onClick={() => toggleOpen(field.key)}
                                  >
                                    {openKeys.has(field.key) ? 'Close' : 'Edit'}
                                  </button>
                                  <ActionMenu
                                    label="Question actions"
                                    items={[
                                      { label: 'Duplicate', onClick: () => duplicate(si, fi) },
                                      {
                                        label: 'Move up',
                                        disabled: fi === 0,
                                        onClick: () => setSections(swapFields(sections, si, fi, -1)),
                                      },
                                      {
                                        label: 'Move down',
                                        disabled: fi === section.fields.length - 1,
                                        onClick: () => setSections(swapFields(sections, si, fi, 1)),
                                      },
                                      sections.length > 1 && {
                                        label: 'Move to section…',
                                        onClick: () => setMoveTarget({ si, fi }),
                                      },
                                      {
                                        label: 'Remove question',
                                        tone: 'danger' as const,
                                        onClick: () =>
                                          setSections(
                                            sections.map((s, i) =>
                                              i === si
                                                ? {
                                                    ...s,
                                                    fields: s.fields.filter((_, j) => j !== fi),
                                                  }
                                                : s
                                            )
                                          ),
                                      },
                                    ]}
                                  />
                                </div>
                                {openKeys.has(field.key) && (
                                  <div className="border-t border-gray-200 bg-gray-50 p-3">
                                    <FieldEditor
                                      field={field}
                                      linkKind={content.link_kind}
                                      typeOptions={typeOptions}
                                      onChange={(patch) => updateField(si, fi, patch)}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </SortableItem>
                        ))}
                      </div>
                    </SortableContext>

                    {addingIn === section.key ? (
                      <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
                        <div className="mb-2 text-sm font-medium text-gray-800">
                          What kind of answer?
                        </div>
                        <div className="grid gap-2 sm:grid-cols-3">
                          {FIELD_TYPES.filter(
                            // A meter reading has nothing to read against
                            // unless the form is filled in against a machine.
                            // Hidden rather than offered and then refused.
                            (t) => t !== 'meter_reading' || content.link_kind === 'asset'
                          ).map((t) => (
                            <button
                              key={t}
                              type="button"
                              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm transition-colors hover:border-brand-600 hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 max-sm:min-h-11"
                              onClick={() => addField(si, t)}
                            >
                              <span className="block text-sm font-medium text-gray-900">
                                {FIELD_TYPE_LABEL[t]}
                              </span>
                              <span className="mt-0.5 block text-xs text-gray-600">
                                {FIELD_TYPE_HINT[t]}
                              </span>
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn-secondary btn-sm mt-3"
                          onClick={() => setAddingIn(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn-secondary btn-sm mt-3"
                        onClick={() => setAddingIn(section.key)}
                      >
                        Add question
                      </button>
                    )}
                  </div>
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        className="btn-secondary btn-sm mt-4"
        onClick={() => setSections([...sections, { key: newKey('s'), title: '', fields: [] }])}
      >
        Add section
      </button>
      </div>

      {moveTarget && (
        <MoveToSectionModal
          sections={sections}
          from={moveTarget}
          onClose={() => setMoveTarget(null)}
          onMove={(toSi) => {
            setSections(moveFieldToSection(sections, moveTarget.si, moveTarget.fi, toSi));
            setMoveTarget(null);
          }}
        />
      )}
    </>
  );
}

/** "Choose one · 3 options · Required": the collapsed row's summary. */
function fieldChip(field: FormField): string {
  const parts = [FIELD_TYPE_LABEL[field.type]];
  if (field.type === 'choice' || field.type === 'checklist') {
    const n = (field.options ?? []).filter((o) => o.trim() !== '').length;
    parts.push(`${n} option${n === 1 ? '' : 's'}`);
  }
  if (field.required) parts.push('Required');
  return parts.join(' · ');
}

/**
 * One sortable wrapper for sections and question rows. The HANDLE is the only
 * thing that drags — the row's own buttons and inputs stay buttons and
 * inputs — and it is a real button, so dnd-kit's keyboard sensor gives
 * space-to-lift and arrows-to-move for free.
 */
function SortableItem({
  id,
  handleLabel,
  children,
}: {
  id: string;
  handleLabel: string;
  children: (handle: ReactNode) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const handle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className="btn-icon cursor-grab touch-none"
      aria-label={handleLabel}
      {...attributes}
      {...listeners}
    >
      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <circle cx="7" cy="5" r="1.5" />
        <circle cx="13" cy="5" r="1.5" />
        <circle cx="7" cy="10" r="1.5" />
        <circle cx="13" cy="10" r="1.5" />
        <circle cx="7" cy="15" r="1.5" />
        <circle cx="13" cy="15" r="1.5" />
      </svg>
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'relative z-10 opacity-75' : undefined}
    >
      {children(handle)}
    </div>
  );
}

/**
 * The form as it reads on a phone — rendered by the REAL field component with
 * throwaway local answers (two renderers would drift), inside a 375px frame
 * by default, because "as it reads on a phone" was the claim this tab always
 * made and desktop width was not keeping it.
 */
function TemplatePreview({ content, dirty }: { content: FormContent; dirty: boolean }) {
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [width, setWidth] = useState<'phone' | 'full'>('phone');

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={width === 'phone' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
          aria-pressed={width === 'phone'}
          onClick={() => setWidth('phone')}
        >
          Phone width
        </button>
        <button
          type="button"
          className={width === 'full' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
          aria-pressed={width === 'full'}
          onClick={() => setWidth('full')}
        >
          Full width
        </button>
      </div>
      {dirty && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This preview includes unsaved edits.
        </div>
      )}
      <div
        className={
          width === 'phone'
            ? 'mx-auto w-[375px] max-w-full rounded-xl border border-gray-300 bg-paper p-3'
            : 'max-w-2xl'
        }
      >
        <FormSections
          content={content}
          answers={answers}
          files={[]}
          meters={[]}
          // No draft exists: an attempted photo upload fails with its own
          // message, which is honest — the preview is for reading.
          submissionId=""
          readOnly={false}
          onChange={(key, value) => setAnswers((prev) => ({ ...prev, [key]: value }))}
        />
        {content.sections.every((s) => s.fields.length === 0) && (
          <p className="muted">No questions yet. Add some on the Build tab.</p>
        )}
      </div>
    </div>
  );
}

/**
 * The frozen history: what an operator was asked in March, without psql.
 * Each entry opens the full document, rendered read-only by the same
 * components as everything else.
 */
function VersionsTab({
  templateId,
  versions,
}: {
  templateId: string;
  versions: FormVersionSummary[];
}) {
  const [open, setOpen] = useState<FormVersion | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function view(versionId: string) {
    try {
      const result = await forms.getTemplateVersion(templateId, versionId);
      setOpen(result.version);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that version.');
    }
  }

  if (versions.length === 0) {
    return <p className="muted">Nothing published yet. Publishing saves a frozen copy here.</p>;
  }

  return (
    <div className="max-w-2xl">
      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}
      <ul className="m-0 mb-4 list-none space-y-2 p-0">
        {versions.map((v) => (
          <li key={v.id} className="flex flex-wrap items-center gap-2 rounded border border-gray-200 p-3">
            <span className="text-sm font-medium">Version {v.version}</span>
            {v.isCurrent && <StatusBadge kind="expiry" status="ok" label="Current" />}
            <span className="text-sm text-gray-600">
              {new Date(v.publishedAt).toLocaleDateString('en-AU')}
              {v.publishedByName ? ` · ${v.publishedByName}` : ''}
            </span>
            <span className="spacer" />
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => void view(v.id)}
            >
              {open?.id === v.id ? 'Shown below' : 'View'}
            </button>
          </li>
        ))}
      </ul>
      <p className="mb-4 text-sm text-gray-600">
        Publishing saves a frozen copy of the form. Anything filled in stays tied to the version
        it was filled against, whatever changes later.
      </p>
      {open && (
        <div>
          <h2 className="mb-3 text-base font-semibold text-gray-900">
            Version {open.version} · {open.content.title || 'Form'}
          </h2>
          <FormSections
            content={open.content}
            answers={{}}
            files={[]}
            meters={[]}
            submissionId=""
            readOnly
          />
        </div>
      )}
    </div>
  );
}

/** The question the Move-to-section dialog is about, and where it lands. */
function MoveToSectionModal({
  sections,
  from,
  onClose,
  onMove,
}: {
  sections: FormContent['sections'];
  from: { si: number; fi: number };
  onClose: () => void;
  onMove: (toSi: number) => void;
}) {
  const [target, setTarget] = useState<number | ''>('');
  const label = sections[from.si]?.fields[from.fi]?.label || 'Untitled question';

  return (
    <Modal
      title="Move to section"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={target === ''}
            onClick={() => target !== '' && onMove(target)}
          >
            Move it
          </button>
        </>
      }
    >
      <p className="mb-3 text-sm text-gray-700">
        &ldquo;{label}&rdquo; moves to the end of the section you pick.
      </p>
      <label className="form-label" htmlFor="move-target-section">
        Section
      </label>
      <select
        id="move-target-section"
        className="form-select"
        value={target === '' ? '' : String(target)}
        onChange={(e) => setTarget(e.target.value === '' ? '' : Number(e.target.value))}
      >
        <option value="">Choose…</option>
        {sections.map((s, i) =>
          i === from.si ? null : (
            <option key={s.key} value={i}>
              {s.title || `Section ${i + 1}`}
            </option>
          )
        )}
      </select>
    </Modal>
  );
}

function FieldEditor({
  field,
  linkKind,
  typeOptions,
  onChange,
}: {
  field: FormField;
  linkKind: FormContent['link_kind'];
  /** The machine-types list, for a machine question's own filter grid. */
  typeOptions: MachineType[] | null;
  onChange: (patch: Partial<FormField>) => void;
}) {
  const isList = field.type === 'choice' || field.type === 'checklist';

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <label className="form-label" htmlFor={`label-${field.key}`}>
            Question
          </label>
          <input
            id={`label-${field.key}`}
            className="form-input"
            value={field.label}
            placeholder="Any visible damage?"
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </div>

        <div>
          <label className="form-label" htmlFor={`type-${field.key}`}>
            Answer
          </label>
          <select
            id={`type-${field.key}`}
            className="form-select"
            value={field.type}
            onChange={(e) => {
              const type = e.target.value as FieldType;
              // A fresh choice question starts as Yes / No / N/A — what
              // almost every prestart question actually wants (round 28).
              // Only when there are no options yet; a typed list is never
              // overwritten by changing type back and forth.
              if (type === 'choice' && (field.options ?? []).filter((o) => o !== '').length === 0) {
                onChange({ type, options: [...YES_NO_NA] });
              } else if (type === 'entity_ref' && !field.entity) {
                // A default rather than a blank: the normalizer DROPS an
                // entity question with no entity, so a blank here would make
                // the question quietly vanish on save.
                onChange({ type, entity: 'machine' });
              } else {
                onChange({ type });
              }
            }}
          >
            {FIELD_TYPES.filter(
              // A meter reading has nothing to read against unless the form is
              // filled in against a machine. Hidden rather than offered and
              // then refused at publish.
              (t) => t !== 'meter_reading' || linkKind === 'asset'
            ).map((t) => (
              <option key={t} value={t}>
                {FIELD_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="mt-1 text-xs text-gray-600">{FIELD_TYPE_HINT[field.type]}</p>

      {isList && (
        <div className="mt-2">
          <label className="form-label" htmlFor={`options-${field.key}`}>
            Options, one per line
          </label>
          <textarea
            id={`options-${field.key}`}
            className="form-input"
            rows={3}
            value={(field.options ?? []).join('\n')}
            onChange={(e) =>
              onChange({ options: e.target.value.split('\n').map((o) => o.trim()) })
            }
          />
          {field.type === 'choice' && (
            <button
              type="button"
              className="btn-ghost btn-sm mt-1"
              onClick={() => onChange({ options: [...YES_NO_NA] })}
            >
              Use Yes / No / N/A
            </button>
          )}
        </div>
      )}

      {field.type === 'number' && (
        <div className="mt-2">
          <label className="form-label" htmlFor={`unit-${field.key}`}>
            Unit
          </label>
          <input
            id={`unit-${field.key}`}
            className="form-input"
            value={field.unit ?? ''}
            placeholder="litres, mm, %"
            onChange={(e) => onChange({ unit: e.target.value })}
          />
        </div>
      )}

      {field.type === 'text' && (
        <label className="check-label-inline mt-2">
          <input
            type="checkbox"
            className="form-check"
            checked={field.multiline === true}
            onChange={(e) => onChange({ multiline: e.target.checked })}
          />
          Room for a few lines
        </label>
      )}

      {field.type === 'photo' && (
        <div className="mt-2">
          <label className="form-label" htmlFor={`max-${field.key}`}>
            How many photos at most
          </label>
          <input
            id={`max-${field.key}`}
            className="form-input"
            inputMode="numeric"
            value={String(field.max_photos ?? 10)}
            onChange={(e) => onChange({ max_photos: Number(e.target.value) || 1 })}
          />
        </div>
      )}

      {field.type === 'entity_ref' && (
        <div className="mt-2 space-y-2">
          <div>
            <label className="form-label" htmlFor={`entity-${field.key}`}>
              Pick what
            </label>
            <select
              id={`entity-${field.key}`}
              className="form-select"
              value={field.entity ?? 'machine'}
              onChange={(e) => onChange({ entity: e.target.value as EntityKind })}
            >
              {ENTITY_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kind === 'machine'
                    ? 'A machine'
                    : kind === 'job'
                      ? 'A job'
                      : kind === 'person'
                        ? 'A person'
                        : 'A client'}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-600">
              The answer is a MENTION: it shows on that thing’s own page under “Mentioned in
              these forms”, and never in its main forms list.
            </p>
          </div>
          {field.entity === 'machine' && typeOptions !== null && (
            <div className="rounded border border-gray-200 bg-white p-3">
              <div className="form-label">Which machines this question offers</div>
              <AssetFilterGrid
                filter={field.asset_filter ?? null}
                typeOptions={typeOptions}
                onChange={(next) =>
                  onChange({ asset_filter: next ?? undefined })
                }
              />
            </div>
          )}
        </div>
      )}

      <div className="mt-3">
        <label className="check-label-inline">
          <input
            type="checkbox"
            className="form-check"
            checked={field.required}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          Must be answered
        </label>
      </div>
    </div>
  );
}


/**
 * The tick grid behind "Offered machines" — and, in stage 3, behind a
 * machine question's own filter. LOCAL to this file on purpose: both callers
 * live here, and the point of one component is that the two scopes cannot
 * drift (ZT's call, kept).
 *
 * Nothing ticked = no filter = every machine. Archived types appear only
 * when already ticked, labelled — a live setting must not silently un-show.
 */
function AssetFilterGrid({
  filter,
  typeOptions,
  onChange,
}: {
  filter: AssetFilter | null;
  typeOptions: MachineType[];
  onChange: (next: AssetFilter | null) => void;
}) {
  const kinds = filter?.kinds ?? [];
  const typeIds = filter?.machine_type_ids ?? [];

  function emit(nextKinds: string[], nextTypeIds: string[]) {
    onChange(
      nextKinds.length === 0 && nextTypeIds.length === 0
        ? null
        : { kinds: nextKinds, machine_type_ids: nextTypeIds }
    );
  }

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const shownTypes = typeOptions.filter((t) => !t.archived || typeIds.includes(t.id));

  return (
    <div className="space-y-3">
      <div>
        <div className="form-label">Kinds</div>
        <div className="flex flex-wrap gap-4">
          {(
            [
              ['plant', 'Plant'],
              ['vehicle', 'Vehicles'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="check-label-inline">
              <input
                type="checkbox"
                className="form-check"
                checked={kinds.includes(value)}
                onChange={() => emit(toggle(kinds, value), typeIds)}
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      {shownTypes.length > 0 && (
        <div>
          <div className="form-label">Machine types</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {shownTypes.map((t) => (
              <label key={t.id} className="check-label-inline">
                <input
                  type="checkbox"
                  className="form-check"
                  checked={typeIds.includes(t.id)}
                  onChange={() => emit(kinds, toggle(typeIds, t.id))}
                />
                {t.name}
                {t.archived ? ' (archived)' : ''}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
